import classNames from 'classnames';
import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppContext, useAppContext } from '~/App';
import { Dialog } from '~/components/ui/Dialog';
import { Image } from '~/components/ui/Image';
import { BottomSheet } from '~/components/ui/nucleus/Sheets';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { useLocationHash } from '~/hooks/useLocationHash';
import {
  Group,
  SettingLink,
  SettingsSubpage,
  Tab,
  UserSettingsPopup,
} from '../ui';
import { AccountProfile } from './AccountProfile';
import { DateAndTimeSettings } from './DateAndTimeSettings';
import { Features } from './Features';
import { ManageAssetTypes } from './ManageAssetTypes';
import { ManageContactTypes } from './ManageContactTypes';
import { ManagePlaceTypes } from './ManagePlaceTypes';
/* import { Integrations } from './Integrations'; */
import { Referrals } from './Referrals';
import { SharingPreferences } from './SharingPreferences';
import { Team } from './Team';
import { UpdateAccountPicForm } from './UpdateAccountPicForm';
import {
  ACCOUNT_SETTINGS_PAGE_UNCHANGED,
  resolveAccountSettingsPage,
} from './accountSettingsHash';
export type PageProps = {
  page: SettingPage;
  showPage: Page;
  setShowPage: (page: Page) => void;
  setShowSettings: (show: boolean) => void;
  canManageAccount?: boolean;
};

export type Page =
  | null
  | 'organisation'
  | 'notifications'
  | 'team'
  | 'integrations'
  | 'profilePicture'
  | 'features'
  | 'assetTypes'
  | 'placeTypes'
  | 'contactTypes'
  | 'dateAndTime'
  | 'sharingPreferences'
  | 'referrals';

type SettingPage = {
  name: string;
  description: string;
  page: Page;
  Component: (props: PageProps) => JSX.Element;
};

type SettingLink = {
  name: string;
  description: string;
  linkTo: string;
};

type SettingGroup = {
  title: string;
  pages: (SettingPage | SettingLink)[];
};

const isLink = (page: SettingPage | SettingLink): page is SettingLink =>
  'linkTo' in page;

const settings: SettingGroup[] = [
  {
    title: 'settings.organisationSettings',
    pages: [
      {
        name: 'Organisation Profile',
        description: `Manage your organisation details`,
        page: 'organisation',
        Component: AccountProfile,
      },
      {
        name: 'Team',
        description: '👥 Manage users and permissions',
        page: 'team',
        Component: Team,
      },
      /* 
      TODO: Add these pages when we are sure they all work properly
      {
        name: 'Integrations',
        description: '🔗 Connect external services',
        page: 'integrations',
        Component: Integrations,
      }, */
    ],
  },
  {
    title: 'settings.preferencesAndCustomization',
    pages: [
      {
        name: 'Date and Time',
        description: 'Set time preferences',
        page: 'dateAndTime',
        Component: DateAndTimeSettings,
      },

      {
        name: 'Features',
        description: 'Enable and customize available features.',
        page: 'features',
        Component: Features,
      },
      {
        name: 'Sharing Preferences',
        description: 'Set default sharing with external assignees',
        page: 'sharingPreferences',
        Component: SharingPreferences,
      },
    ],
  },
  {
    title: 'settings.referralProgram',
    pages: [
      {
        name: 'Referrals',
        description: 'Share PropFit and save together',
        page: 'referrals',
        Component: Referrals,
      },
    ],
  },
];

export const AccountSettings = () => {
  const { t } = useTranslation();
  const { hash } = useLocation();
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  const [showSettings, setShowSettings] = useLocationHash('#org-settings-open');
  const [showPage, setShowPage] = useState<Page>(null);
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward');
  const [saveAction, setSaveAction] = useState<(() => void) | null>(null);
  const [placeTypesDirty, setPlaceTypesDirty] = useState(false);
  const [contactTypesDirty, setContactTypesDirty] = useState(false);
  const [assetTypesDirty, setAssetTypesDirty] = useState(false);
  const [showUnsavedPrompt, setShowUnsavedPrompt] = useState(false);
  const [pendingNextPage, setPendingNextPage] = useState<Page | null>(null);
  const bypassUnsavedPromptRef = useRef(false);

  // Determine if user can manage account (used to gate Place Types visibility)
  const appContext = useAppContext();
  type AppContextWithRules = {
    rules?: Array<{ subject: string; action: string }>;
  };
  const contextWithRules = appContext as AppContextWithRules;
  const accountRule = Array.isArray(contextWithRules.rules)
    ? contextWithRules.rules.find((rule) => rule.subject === 'Account')
    : undefined;
  const canManageAccount = accountRule?.action === 'manage';

  const updateHashForPage = (nextPage: Page) => {
    const newHash = nextPage
      ? `#org-settings-open:${nextPage}`
      : '#org-settings-open';
    navigate({ hash: newHash }, { replace: true });
  };

  // Keep subpage in sync when hash changes after mount (e.g., direct links), gated by permissions
  useEffect(() => {
    const nextPage = resolveAccountSettingsPage(hash, {
      canManageAccount,
      showSettings,
    });
    if (nextPage !== ACCOUNT_SETTINGS_PAGE_UNCHANGED) {
      setShowPage(nextPage);
    }
  }, [hash, showSettings, canManageAccount]);
  const { tenant } = useContext(AppContext);

  // (moved canManageAccount computation above so hooks/effects can use it)

  // Initialize pageProps with the canManageAccount value
  const pageProps = {
    backTo: () => setShowPage(null),
    showPage,
    setShowPage,
    canManageAccount, // Use the actual value from context
    setShowSettings,
  };

  const getDepth = (page: Page) => {
    if (!page) return 0; // root
    if (page === 'placeTypes' || page === 'contactTypes') return 2; // sub-sub under features
    return 1; // default subpages
  };

  const setShowPageWithHash = (nextPage: Page) => {
    // If we're on a manage page with unsaved changes and navigating away, prompt first
    const leavingManagePage =
      (showPage === 'placeTypes' &&
        nextPage !== 'placeTypes' &&
        placeTypesDirty) ||
      (showPage === 'contactTypes' &&
        nextPage !== 'contactTypes' &&
        contactTypesDirty) ||
      (showPage === 'assetTypes' &&
        nextPage !== 'assetTypes' &&
        assetTypesDirty);

    if (leavingManagePage && !bypassUnsavedPromptRef.current) {
      setPendingNextPage(nextPage);
      setShowUnsavedPrompt(true);
      return; // Abort immediate navigation; wait for user to confirm
    }

    const currentDepth = getDepth(showPage);
    const nextDepth = getDepth(nextPage);
    setDirection(nextDepth > currentDepth ? 'forward' : 'backward');
    setShowPage(nextPage);
    updateHashForPage(nextPage);

    // Clear dirty flags when we've actually navigated away from their pages
    if (showPage === 'placeTypes' && nextPage !== 'placeTypes') {
      setPlaceTypesDirty(false);
    }
    if (showPage === 'contactTypes' && nextPage !== 'contactTypes') {
      setContactTypesDirty(false);
    }
    if (showPage === 'assetTypes' && nextPage !== 'assetTypes') {
      setAssetTypesDirty(false);
    }
  };

  const getPageProps = (page: SettingPage) => ({
    page,
    ...pageProps,
    setShowPage: setShowPageWithHash,
  });

  const handleSaveReady = useCallback((save: () => void) => {
    setSaveAction(() => save);
  }, []);

  // Reset all dirty flags when settings modal closes completely
  // This ensures we start fresh on next open
  useEffect(() => {
    if (!showSettings) {
      setPlaceTypesDirty(false);
      setContactTypesDirty(false);
      setAssetTypesDirty(false);
      setSaveAction(null);
      bypassUnsavedPromptRef.current = false;
    }
  }, [showSettings]);

  // After a successful save clears the dirty flag, complete any pending navigation
  useEffect(() => {
    if (!pendingNextPage) return;

    const relevantDirty =
      (showPage === 'placeTypes' && placeTypesDirty) ||
      (showPage === 'contactTypes' && contactTypesDirty) ||
      (showPage === 'assetTypes' && assetTypesDirty);

    // Only proceed once the current page is no longer dirty
    if (relevantDirty) return;

    const currentDepth = getDepth(showPage);
    const nextDepth = getDepth(pendingNextPage);
    setDirection(nextDepth > currentDepth ? 'forward' : 'backward');
    setShowPage(pendingNextPage);
    updateHashForPage(pendingNextPage);

    // Clear the dirty flag for the page we left
    if (showPage === 'placeTypes') setPlaceTypesDirty(false);
    if (showPage === 'contactTypes') setContactTypesDirty(false);
    if (showPage === 'assetTypes') setAssetTypesDirty(false);

    setPendingNextPage(null);
  }, [
    pendingNextPage,
    placeTypesDirty,
    contactTypesDirty,
    assetTypesDirty,
    showPage,
  ]);

  const settingsContent = (
    <div className='flex flex-col items-center p-6 pt-0 lg:p-10 lg:pt-0'>
      <div
        className='h-24 w-24'
        onClick={() =>
          canManageAccount && setShowPageWithHash('profilePicture')
        }
      >
        <Image
          objectFit='contain'
          size='fit'
          url={tenant?.image}
          className={classNames('rounded-sm', {
            'cursor-pointer': canManageAccount,
          })}
          alt={tenant?.name || 'Account'}
        />
      </div>
      {(() => {
        const flatPages: SettingPage[] = [
          ...settings.flatMap((group) =>
            group.pages.filter((p): p is SettingPage => !isLink(p))
          ),
          {
            name: 'Edit Profile Picture',
            description: '',
            page: 'profilePicture',
            Component: UpdateAccountPicForm,
          },
          // Only include Manage Place/Contact Types if user can manage account
          ...(canManageAccount
            ? ([
                {
                  name: 'Manage Place Types',
                  description: 'Create and edit place types',
                  page: 'placeTypes',
                  // Cast to satisfy Component signature; we render manually below.
                  Component: ManagePlaceTypes as unknown as (
                    props: PageProps
                  ) => JSX.Element,
                },
                {
                  name: 'Manage Asset Types',
                  description: 'Create and edit asset types',
                  page: 'assetTypes',
                  Component: ManageAssetTypes as unknown as (
                    props: PageProps
                  ) => JSX.Element,
                },
                {
                  name: 'Manage Contact Types',
                  description: 'Create and edit contact types',
                  page: 'contactTypes',
                  // Cast to satisfy Component signature; we render manually below.
                  Component: ManageContactTypes as unknown as (
                    props: PageProps
                  ) => JSX.Element,
                },
              ] as SettingPage[])
            : ([] as SettingPage[])),
        ];

        const selected = flatPages.find((p) => p.page === showPage) || null;
        if (!selected) return null;

        const formNode =
          selected.page === 'profilePicture' ? (
            <UpdateAccountPicForm
              page={{
                name: 'Edit Profile Picture',
                description: '',
                page: 'profilePicture',
                Component: UpdateAccountPicForm,
              }}
              showPage={showPage}
              setShowPage={setShowPage}
              setShowSettings={setShowSettings}
              canManageAccount={canManageAccount}
            />
          ) : selected.page === 'placeTypes' && canManageAccount ? (
            <ManagePlaceTypes
              onClose={() => setShowSettings(false)}
              useExternalActions={true}
              onSaveReady={handleSaveReady}
              onDirtyChange={setPlaceTypesDirty}
            />
          ) : selected.page === 'assetTypes' && canManageAccount ? (
            <ManageAssetTypes
              onClose={() => setShowSettings(false)}
              useExternalActions={true}
              onSaveReady={handleSaveReady}
              onDirtyChange={setAssetTypesDirty}
            />
          ) : selected.page === 'contactTypes' && canManageAccount ? (
            <ManageContactTypes
              onClose={() => setShowSettings(false)}
              useExternalActions={true}
              onSaveReady={handleSaveReady}
              onDirtyChange={setContactTypesDirty}
            />
          ) : (
            <selected.Component {...getPageProps(selected)} />
          );

        const handleBack = () => {
          if (
            selected.page === 'placeTypes' ||
            selected.page === 'contactTypes' ||
            selected.page === 'assetTypes'
          ) {
            setShowPageWithHash('features');
            return;
          }
          setShowPageWithHash(null);
        };

        return (
          <SettingsSubpage
            title={selected.name}
            isOpen={!!selected}
            onClose={() => {
              if (
                ((selected.page === 'placeTypes' && placeTypesDirty) ||
                  (selected.page === 'contactTypes' && contactTypesDirty) ||
                  (selected.page === 'assetTypes' && assetTypesDirty)) &&
                !bypassUnsavedPromptRef.current
              ) {
                setShowUnsavedPrompt(true);
                return;
              }
              // Clear bypass once we've allowed a close
              bypassUnsavedPromptRef.current = false;
              setShowSettings(false);
            }}
            backTo={handleBack}
            form={formNode}
            action={
              (selected.page === 'placeTypes' ||
                selected.page === 'contactTypes' ||
                selected.page === 'assetTypes') &&
              saveAction
                ? {
                    label: 'Save changes',
                    intent: 'primary',
                    onClick: () => {
                      saveAction();
                      // Child components will handle closing after successful save
                      // and will call onDirtyChange(false) to clear the dirty flag
                    },
                  }
                : undefined
            }
            direction={direction}
            contentKey={selected.page || ''}
          />
        );
      })()}
      <p className='my-2 text-lg font-semibold text-primary'>
        {tenant?.name || 'Account'}
      </p>

      {settings.map((group) => (
        <Fragment key={group.title}>
          <Group title={t(group.title)} />
          {group.pages.map((page) =>
            isLink(page) ? (
              <SettingLink
                key={page.name}
                {...page}
                setShowSettings={setShowSettings}
              />
            ) : (
              <Page
                key={page.name}
                {...getPageProps(page)}
                setShowSettings={setShowSettings}
              />
            )
          )}
        </Fragment>
      ))}
    </div>
  );

  // If we're in a bottom sheet (mobile), return content directly
  if (isMobile) {
    return (
      <>
        <BottomSheet
          isOpen={showSettings}
          onClose={() => {
            if (
              ((showPage === 'placeTypes' && placeTypesDirty) ||
                (showPage === 'contactTypes' && contactTypesDirty) ||
                (showPage === 'assetTypes' && assetTypesDirty)) &&
              !bypassUnsavedPromptRef.current
            ) {
              setShowUnsavedPrompt(true);
              return;
            }
            bypassUnsavedPromptRef.current = false;
            setShowSettings(false);
          }}
          padding='none'
        >
          {settingsContent}
        </BottomSheet>
        {showUnsavedPrompt && (
          <Dialog
            show={showUnsavedPrompt}
            title={'Unsaved changes'}
            onClose={() => setShowUnsavedPrompt(false)}
          >
            <div className='mx-4 my-6 text-center text-sm text-copy-alt'>
              You have unsaved changes. What would you like to do?
            </div>
            <div className='m-4 flex items-center justify-end gap-3'>
              <button
                type='button'
                className='rounded-md bg-delete px-3 py-2 text-xs uppercase text-white shadow'
                onClick={() => {
                  setShowUnsavedPrompt(false);
                  if (pendingNextPage) {
                    // Discard changes and navigate to the intended page
                    if (showPage === 'placeTypes') setPlaceTypesDirty(false);
                    if (showPage === 'contactTypes')
                      setContactTypesDirty(false);
                    if (showPage === 'assetTypes') setAssetTypesDirty(false);
                    const currentDepth = getDepth(showPage);
                    const nextDepth = getDepth(pendingNextPage);
                    setDirection(
                      nextDepth > currentDepth ? 'forward' : 'backward'
                    );
                    setShowPage(pendingNextPage);
                    updateHashForPage(pendingNextPage);
                    setPendingNextPage(null);
                  } else {
                    setShowSettings(false);
                  }
                }}
              >
                Discard
              </button>
              <button
                type='button'
                className='rounded-md bg-brand px-3 py-2 text-xs uppercase text-white shadow'
                onClick={() => {
                  setShowUnsavedPrompt(false);
                  saveAction?.();
                }}
              >
                Save changes
              </button>
            </div>
          </Dialog>
        )}
      </>
    );
  }

  // If we're on desktop, wrap in the popup
  return (
    <>
      <UserSettingsPopup
        isOpen={showSettings}
        onClose={() => {
          if (
            ((showPage === 'placeTypes' && placeTypesDirty) ||
              (showPage === 'contactTypes' && contactTypesDirty) ||
              (showPage === 'assetTypes' && assetTypesDirty)) &&
            !bypassUnsavedPromptRef.current
          ) {
            setShowUnsavedPrompt(true);
            return;
          }
          bypassUnsavedPromptRef.current = false;
          setShowSettings(false);
        }}
      >
        {settingsContent}
      </UserSettingsPopup>
      {showUnsavedPrompt && (
        <Dialog
          show={showUnsavedPrompt}
          title={'Unsaved changes'}
          onClose={() => setShowUnsavedPrompt(false)}
        >
          <div className='mx-4 my-6 text-center text-sm text-copy-alt'>
            You have unsaved changes. What would you like to do?
          </div>
          <div className='m-4 flex items-center justify-end gap-3'>
            <button
              type='button'
              className='text-xs uppercase'
              onClick={() => {
                setShowUnsavedPrompt(false);
                setPendingNextPage(null);
              }}
            >
              Cancel
            </button>
            <button
              type='button'
              className='rounded-md bg-delete px-3 py-2 text-xs uppercase text-white shadow'
              onClick={() => {
                setShowUnsavedPrompt(false);
                if (pendingNextPage) {
                  // Discard changes and navigate to the intended page
                  if (showPage === 'placeTypes') setPlaceTypesDirty(false);
                  if (showPage === 'contactTypes') setContactTypesDirty(false);
                  if (showPage === 'assetTypes') setAssetTypesDirty(false);
                  const currentDepth = getDepth(showPage);
                  const nextDepth = getDepth(pendingNextPage);
                  setDirection(
                    nextDepth > currentDepth ? 'forward' : 'backward'
                  );
                  setShowPage(pendingNextPage);
                  updateHashForPage(pendingNextPage);
                  setPendingNextPage(null);
                } else {
                  setShowSettings(false);
                }
              }}
            >
              Discard
            </button>
            <button
              type='button'
              className='rounded-md bg-brand px-3 py-2 text-xs uppercase text-white shadow'
              onClick={() => {
                setShowUnsavedPrompt(false);
                saveAction?.();
              }}
            >
              Save changes
            </button>
          </div>
        </Dialog>
      )}
    </>
  );
};

function Page(props: PageProps) {
  const { page, setShowPage } = props;

  return (
    <>
      <Tab
        title={page.name}
        description={page.description}
        onClick={() => setShowPage(page.page)}
      />
    </>
  );
}
